import React, { Component } from 'react'
import { observer } from 'mobx-react'
import * as R from 'ramda'
import i18n from 'i18next'
import Modal from '../../UniformComponents/Modal'
import Typography from '../../UniformComponents/Typography'
import Button from '../../UniformComponents/Button'
import InfoAccordion from '../InfoAccordion'
import CheckboxGroup from '../form/CheckboxGroup'
import './benchmarkingModal.scss'
import exploriLogo from '../../assets/images/explori_logo.svg'
import { capitalizeFirstLetter } from '../../utils/helpers'

@observer
class BenchmarkingModalShell extends Component {
    constructor(props) {
        super(props)
        const { periodOptions, selectedPeriods } = props
        this.state = {
            activeTab: 'explori',
            activePeriod: selectedPeriods?.[0] ?? periodOptions?.[0]?.value,
            changeUncontrolledInputCount: 0,
        }
    }

    setActiveTab = (tab) => {
        this.setState({ activeTab: tab })
    }

    setActivePeriod = (value) => {
        const { changePeriods } = this.props
        this.setState({ activePeriod: value })
        changePeriods?.({ value: [value] })
    }

    onBenchmarkChange = (checkboxGroupOptions, { value: newSelections }) => {
        const { selectedBenchmarks = [], changeBenchmarks } = this.props
        const otherSelections = selectedBenchmarks.filter((b) => !checkboxGroupOptions.includes(b))
        changeBenchmarks?.({ value: [...otherSelections, ...newSelections] })
    }

    onReset = () => {
        const { changeBenchmarks, changeYears } = this.props
        changeBenchmarks?.({ value: [] })
        changeYears?.({ value: [] })
        this.setState(({ changeUncontrolledInputCount }) => ({ changeUncontrolledInputCount: changeUncontrolledInputCount + 1 }))
    }

    onRemoveBenchmark = (val) => {
        const { selectedBenchmarks = [], changeBenchmarks } = this.props
        changeBenchmarks?.({ value: selectedBenchmarks.filter((b) => b !== val) })
        this.setState(({ changeUncontrolledInputCount }) => ({ changeUncontrolledInputCount: changeUncontrolledInputCount + 1 }))
    }

    onRemoveYear = (val) => {
        const { selectedYears = [], changeYears } = this.props
        changeYears?.({ value: selectedYears.filter((y) => y !== val) })
        this.setState(({ changeUncontrolledInputCount }) => ({ changeUncontrolledInputCount: changeUncontrolledInputCount + 1 }))
    }
    
    buildCustomBenchmarkGroups = (formattedTags = [], storeType, benchmarkOptions = []) => {
    // uses 'formatCustomBenchmarkTags' input to build {tab, groupName, options:[]}
        switch (storeType) {
            case 'dashboard':
                return formattedTags.flatMap(({ customTagName, scopes, tagOptions }) => scopes.map((scope) => ({
                    tab: ['industry', 'sector'].includes(scope) ? 'explori' : 'company',
                    groupName: customTagName,
                    options: tagOptions.map((top) => ({
                        label: top.tagOptionName,
                        value: `${scope}_${top.tagOptionId}`,
                    })),
                })))
            case 'report': {
                // 1) In report, different from dashboard, benchmark and custom benchmark values from
                // report/v1/:surveyId/benchmark, come with keys `scope (tagName - tagOptionName)` (no ids)
                // 2) customBenchmarkTags from GET `/benchmarkTags` returns all tags no matter if they have data to
                // display (or ) so we build a lookup to check if they exist in `benchmarkOptions`
                const optionsWithValuesLookup = new Set(benchmarkOptions.map((o) => o.value))
                return formattedTags.flatMap(({ customTagName, scopes = [], tagOptions }) => scopes.flatMap((scope) => {
                    const options = tagOptions
                        .map((top) => ({
                            label: top.tagOptionName,
                            value: `${scope} (${customTagName} - ${top.tagOptionName})`,
                        }))
                        .filter((opt) => optionsWithValuesLookup.has(opt.value))
                    if (options.length === 0) return []
                    return [{
                        tab: ['industry', 'sector'].includes(scope) ? 'explori' : 'company',
                        groupName: customTagName,
                        options,
                    }]
                }))
            }
            default:
                // e.g. dataExplorer (no custom benchmarks)
                return []
        }
    }

    formatCustomBenchmarkTags = (rawTags, storeType, langcode) => rawTags.flatMap((tag) => {
        // simplifies and parses JSON fields in customTags from `/benchmarkTags`
        const config = tag.parentConfig || tag.customTagConfig
        if (!config) return []
        const parsedConfig = JSON.parse(config)
        if (!parsedConfig.benchmarkOptions?.includes('publish')) return []
        const scopes = parsedConfig.benchmarkOptions.filter((s) => s !== 'publish')
        const tagOptions = storeType === 'report'
            ? tag.tagOptions.map((top) => {
                const parsedTagConfig = JSON.parse(top.tagOptionConfig)
                const tagName = R.pathOr(
                    R.pathOr(
                        R.pathOr('', ['langs', 'en_GB'], parsedTagConfig),
                        ['langs', langcode],
                        parsedTagConfig,
                    ),
                    ['name'], parsedTagConfig,
                )
                return { tagOptionId: top.tagOptionId, tagOptionName: tagName }
            })
            : tag.tagOptions // dashboard already formats tags as such
        return [{ customTagName: tag.customTagName, customTagId: tag.customTagId, scopes, tagOptions }]
    })

    buildBenchmarkCards = (benchmarkOptions, customBenchmarkGroups = []) => {
        const standardExplori = []
        const sectorOptions = []
        const standardCompany = []

        benchmarkOptions.forEach((option) => {
            const parensIdx = option.label.indexOf(' (')
            if (option.value === 'industry') {
                standardExplori.push({
                    ...option,
                    label: i18n.t('benchmarkModal.industry'),
                    description: i18n.t('benchmarkModal.industry.desc'),
                })
            } else if (option.value === 'sector') {
                // Sector goes into Explori tab as separate card. Format: "Sector ([sector name])"
                const sectorLabel = parensIdx >= 0 ? option.label.slice(parensIdx + 2, -1) : option.label
                sectorOptions.push({ label: capitalizeFirstLetter(sectorLabel), value: 'sector' })
            } else {
                standardCompany.push(option)
            }
        })

        const formattedCustomBenchmarks = { explori: [], company: [] }
        customBenchmarkGroups.forEach(({ tab, groupName, options }) => {
            formattedCustomBenchmarks[tab]?.push({ groupName, options })
        })

        const buildCards = (standard, sector, formattedCustom = []) => [
            ...(standard.length > 0 ? [{
                groupName: null, // first standard card title has custom names
                options: standard,
            }] : []),
            ...(sector.length > 0 ? [{ groupName: 'Sector', options: sector }] : []),
            ...formattedCustom, // from 'buildCustomBenchmarkGroups'
        ]

        return {
            explori: buildCards(standardExplori, sectorOptions, formattedCustomBenchmarks.explori),
            company: buildCards(standardCompany, [], formattedCustomBenchmarks.company),
        }
    }

    buildBenchmarkLabelLookup = (benchmarkCards) => Object.fromEntries(
        [...benchmarkCards.explori, ...benchmarkCards.company]
            .flatMap((card) => card.options)
            .map((o) => [o.value, o.label]),
    )

    renderPeriodToggle = () => {
        const { periodOptions } = this.props
        const { activePeriod } = this.state
        if (!periodOptions?.length) return null
        return (
            <div className="bm-period-toggle">
                {periodOptions.map((option, i) => (
                    <button
                        key={option.value}
                        type="button"
                        className={[
                            'bm-period-btn',
                            i === 0 ? 'left' : 'right',
                            activePeriod === option.value ? 'active' : '',
                        ].join(' ')}
                        onClick={() => this.setActivePeriod(option.value)}
                    >
                        {option.label}
                    </button>
                ))}
            </div>
        )
    }

    renderBenchmarkPanel = () => {
        const { activeTab, changeUncontrolledInputCount } = this.state
        const {
            benchmarkOptions = [],
            selectedBenchmarks = [],
            customBenchmarkTags,
            langcode,
            storeType,
            industry,
        } = this.props

        const formattedTags = this.formatCustomBenchmarkTags(customBenchmarkTags ?? [], storeType, langcode)
        const customGroups = this.buildCustomBenchmarkGroups(formattedTags, storeType, benchmarkOptions)

        // Remove custom benchmark options from standard company card: already formatted in customGroups
        const customGroupValueLookup = new Set(customGroups.flatMap(({ options }) => options.map((o) => o.value)))
        const standardOptions = benchmarkOptions.filter((o) => !customGroupValueLookup.has(o.value))

        const benchmarkCards = this.buildBenchmarkCards(standardOptions, customGroups)
        const exploriTabValues = new Set(benchmarkCards.explori.flatMap((c) => c.options.map((o) => o.value)))
        const companyTabValues = new Set(benchmarkCards.company.flatMap((c) => c.options.map((o) => o.value)))
        const hasBenchmarks = exploriTabValues.size > 0 || companyTabValues.size > 0
        const exploriCount = selectedBenchmarks.filter((b) => exploriTabValues.has(b)).length
        const companyCount = selectedBenchmarks.filter((b) => companyTabValues.has(b)).length
        const showExploriCards = activeTab === 'explori' && exploriTabValues.size > 0
        const showCompanyCards = activeTab === 'company' && companyTabValues.size > 0

        return (
            <div className="bm-left">
                <div className="bm-left-header">
                    <span className="bm-section-label">{i18n.t('benchmarkModal.benchmarks')}</span>
                    {hasBenchmarks && this.renderPeriodToggle()}
                </div>
                <div className="bm-tabs">
                    {exploriTabValues.size > 0 && (
                        <button
                            type="button"
                            className={`bm-tab ${activeTab === 'explori' ? 'active' : ''}`}
                            onClick={() => this.setActiveTab('explori')}
                        >
                            {industry
                                ? i18n.t('benchmarkModal.tab.industry', { industry })
                                : 'Explori'
                            }
                            {exploriCount > 0 && <span className="bm-tab-badge">{exploriCount}</span>}
                        </button>
                    )}
                    {companyTabValues.size > 0 && (
                        <button
                            type="button"
                            className={`bm-tab ${activeTab === 'company' ? 'active' : ''}`}
                            onClick={() => this.setActiveTab('company')}
                        >
                            {i18n.t('benchmarkModal.tab.company')}
                            {companyCount > 0 && <span className="bm-tab-badge">{companyCount}</span>}
                        </button>
                    )}
                </div>
                <div className="content">
                    <div className="scrollable-container">
                        <div className="bm-tab-desc-wrapper">
                            {hasBenchmarks && (
                                <p className="bm-tab-desc">
                                    {activeTab === 'explori'
                                        ? i18n.t('benchmarkModal.desc.explori')
                                        : i18n.t('benchmarkModal.desc.company')}
                                </p>
                            )}
                            <InfoAccordion
                                title={i18n.t(
                                    hasBenchmarks
                                        ? 'benchmarkModal.desc.explori.accordion'
                                        : 'benchmarkModal.noBenchmarks',
                                )}
                                cssClass={hasBenchmarks ? '' : 'warning'}
                                icon="fa-light fa-circle-info"
                                hideChevron
                            />
                        </div>
                        <div className="bm-benchmark-grid">
                            {/* Explori tab */}
                            {showExploriCards && benchmarkCards.explori.map((card) => (
                                <div className="bm-card" key={card.groupName ?? 'standard-card'}>
                                    {card.groupName === null && (
                                        <div className="logo"><img src={exploriLogo} alt="Explori Logo" /></div>
                                    )}
                                    <CheckboxGroup
                                        key={`explori-${card.groupName ?? 'standard'}-${changeUncontrolledInputCount}`}
                                        name={`explori-benchmarks-${card.groupName ?? 'standard'}`}
                                        label={card.groupName ?? undefined}
                                        options={card.options}
                                        defaultChecked={selectedBenchmarks.slice()}
                                        onBlur={(val) => {
                                            const tabValues = card.options.map((o) => o.value)
                                            this.onBenchmarkChange(tabValues, val)
                                        }}
                                        cssClass="benchmark-grp"
                                        returnValueUnchanged
                                    />
                                </div>
                            ))}
                            {/* Company, portfolio, division tab */}
                            {showCompanyCards && benchmarkCards.company.map((card) => (
                                <div className="bm-card" key={card.groupName ?? 'standard-card'}>
                                    <CheckboxGroup
                                        key={`company-${card.groupName ?? 'standard'}-${changeUncontrolledInputCount}`}
                                        name={`company-benchmarks-${card.groupName ?? 'standard'}`}
                                        label={card.groupName ?? i18n.t('benchmarkModal.tab.company.standardCardTitle')}
                                        options={card.options}
                                        defaultChecked={selectedBenchmarks.slice()}
                                        onBlur={(val) => {
                                            const tabValues = card.options.map((o) => o.value)
                                            this.onBenchmarkChange(tabValues, val)
                                        }}
                                        cssClass="benchmark-grp"
                                        returnValueUnchanged
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                    {this.renderTray(
                        i18n.t('benchmarkModal.selected.benchmarks'),
                        selectedBenchmarks,
                        this.buildBenchmarkLabelLookup(benchmarkCards),
                        this.onRemoveBenchmark,
                    )}
                </div>
            </div>
        )
    }

    renderYearComparisonPanel = () => {
        const { changeUncontrolledInputCount } = this.state
        const { yearOptions = [], selectedYears = [], changeYears } = this.props
        const yearLabelLookup = Object.fromEntries(yearOptions.map((o) => [o.value, o.label]))
        const sortedSelectedYears = selectedYears
            .slice()
            .sort((a, b) => Number(yearLabelLookup[b] ?? b) - Number(yearLabelLookup[a] ?? a))
        return (
            <div className="bm-right">
                <span className="bm-section-label">{i18n.t('benchmarkModal.comparisons')}</span>
                <p className="bm-subtitle">{i18n.t('benchmarkModal.desc.yoy')}</p>
                <div className="content">
                    {yearOptions.length > 0 && (
                        <div className="scrollable-container" style={{ marginTop: '32px' }}>
                            <div className="bm-card">
                                <CheckboxGroup
                                    key={`years-${changeUncontrolledInputCount}`}
                                    name="years"
                                    label={i18n.t('benchmarkModal.yoy')}
                                    options={yearOptions.slice().sort((a, b) => b.value - a.value)}
                                    defaultChecked={selectedYears.slice()}
                                    onBlur={changeYears}
                                    returnValueUnchanged
                                />
                            </div>
                        </div>
                    )}
                    {this.renderTray(
                        i18n.t('benchmarkModal.selected.comparisons'),
                        sortedSelectedYears,
                        yearLabelLookup,
                        this.onRemoveYear,
                    )}
                </div>
            </div>
        )
    }

    renderFooter = () => {
        const {
            onClose, onApply, selectedBenchmarks = [], selectedYears = [],
        } = this.props
        const selectedCount = selectedBenchmarks.length + selectedYears.length
        const applyDisabled = selectedCount === 0

        return (
            <div className="bm-footer">
                <button type="button" className="bm-reset" onClick={this.onReset}>
                    <i className="fa-light fa-rotate-left" />
                    <span>{i18n.t('reset')}</span>
                </button>
                <div className="bm-footer-right">
                    <span className="bm-selected-count">
                        <strong>{selectedCount}</strong>
                        {' '}
                        {i18n.t('benchmarkModal.selected')}
                    </span>
                    <Button
                        onClick={onClose}
                        label={i18n.t('cancel')}
                        cssClass="tertiary regular"
                    />
                    <Button
                        onClick={onApply ?? onClose}
                        label={i18n.t('benchmarkModal.btn.apply')}
                        cssClass={`regular ${applyDisabled ? 'disabled' : 'primary'}`}
                        disabled={applyDisabled}
                    />
                </div>
            </div>
        )
    }

    renderTray = (title, selectedValues, labelLookup, onRemove) => {
        if (!selectedValues.length) return null
        return (
            <div className="bm-tray">
                <span className="bm-tray-label">{title}</span>
                <div className="bm-tray-pills">
                    {selectedValues.map((value) => {
                        const label = labelLookup[value] ?? value
                        const displayLabel = label.length > 20 ? `${label.slice(0, 20)}…` : label
                        return (
                            <div key={value} className="bm-tray-pill" title={label}>
                                <span>{displayLabel}</span>
                                <button
                                    type="button"
                                    className="bm-tray-pill-remove"
                                    onClick={() => onRemove(value)}
                                >
                                    <i className="fa-light fa-xmark" />
                                </button>
                            </div>
                        )
                    })}
                </div>
            </div>
        )
    }

    render() {
        const { show, onClose, yearOptions } = this.props

        return (
            <Modal show={show} onClose={onClose} cssClass="benchmarkingModal x-large">
                <div className="bm-header">
                    <Typography type="h2">{i18n.t('benchmarkModal.title')}</Typography>
                    <Typography type="p" className="bm-subtitle">{i18n.t('benchmarkModal.desc')}</Typography>
                </div>
                <div className="bm-main-container">
                    {this.renderBenchmarkPanel()}
                    {yearOptions?.length > 0 && this.renderYearComparisonPanel()}
                </div>
                {this.renderFooter()}
            </Modal>
        )
    }
}

export default BenchmarkingModalShell
